import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ValuationClass = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('valuation_classes');
  const [viewItem, setViewItem] = useState(null);
  const [valuationClasses, setValuationClasses] = useState([]);
  const [accountCategoryReferences, setAccountCategoryReferences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedAccountCategoryRef, setSelectedAccountCategoryRef] = useState('');

  useEffect(() => {
    loadValuationClasses();
    loadAccountCategoryReferences();
  }, []);

  const loadValuationClasses = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-classes`);
      setValuationClasses(response.data || []);
    } catch (error) {
      console.error('Error loading valuation classes:', error);
      toast.error('Failed to load valuation classes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccountCategoryReferences = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/account-category-references`);
      setAccountCategoryReferences(response.data || []);
    } catch (error) {
      console.error('Error loading account category references:', error);
    }
  };

  const handleOpenDialog = (valuationClass = null) => {
    setEditingClass(valuationClass);
    if (valuationClass) {
      setSelectedAccountCategoryRef(valuationClass.account_category_reference_id);
    } else {
      setSelectedAccountCategoryRef('');
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      valuation_class_id: (formData.get('valuation_class_id') || '').toUpperCase(),
      description: formData.get('description'),
      account_category_reference_id: selectedAccountCategoryRef
    };

    if (!data.account_category_reference_id) {
      toast.error('Please select an Account Category Reference');
      return;
    }

    try {
      if (editingClass) {
        await axios.put(`${BACKEND_URL}/api/valuation-classes/${editingClass.valuation_class_id}`, {
          description: data.description,
          account_category_reference_id: data.account_category_reference_id
        });
        toast.success('Valuation Class updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/valuation-classes`, data);
        toast.success('Valuation Class created successfully');
      }
      setIsDialogOpen(false);
      loadValuationClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save valuation class');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Valuation Classes</h1>
            <p className="text-sm text-gray-500 mt-1">Manage valuation class configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Valuation Classes</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Valuation Class
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : valuationClasses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No valuation classes found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Category Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {valuationClasses.map((valuationClass) => (
                    <tr key={valuationClass.valuation_class_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{valuationClass.valuation_class_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{valuationClass.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{valuationClass.account_category_reference_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="valuation_classes" onView={() => setViewItem(valuationClass)} onEdit={() => handleOpenDialog(valuationClass)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingClass ? 'Edit Valuation Class' : 'Create Valuation Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="valuation_class_id">Valuation Class ID * (4 digits)</Label>
                <Input
                  id="valuation_class_id"
                  name="valuation_class_id"
                  defaultValue={editingClass?.valuation_class_id || ''}
                  disabled={editingClass !== null}
                  required
                  maxLength={4}
                  minLength={4}
                  pattern="[0-9]{4}"
                  title="Must be exactly 4 digits"
                  className="mt-1"
                  placeholder="e.g., 3000, 3010"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Must be exactly 4 digits</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingClass?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="account_category_reference_id">Account Category Reference *</Label>
                <Select value={selectedAccountCategoryRef} onValueChange={setSelectedAccountCategoryRef} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Account Category Reference" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountCategoryReferences.map((ref) => (
                      <SelectItem key={ref.account_category_reference_id} value={ref.account_category_reference_id}>
                        {ref.account_category_reference_id} - {ref.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingClass ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ValuationClass;
